import React from 'react';
import {Link, useLocation} from 'react-router-dom';
import {Box, Tooltip} from '@mui/material';
import {gql} from '@apollo/client';
import {useQuery} from '@apollo/client/react';
import auth from 'services/auth';
import {adminTokens} from 'theme/adminTokens';
import logo from 'assets/livezone_logo.png';
import {NavIcon} from './NavIcons';
import {NavItem, adminNav, isNavActive, visibleNav, workspaceNav} from './navigation';

const PENDING_ENTRIES = gql`
  query PendingEntryCount {
    total: _getEntriesMeta(filters: {status: "waiting"}) {
      count
    }
  }
`;

const navButtonSx = {
  border: 0,
  background: 'none',
  width: '100%',
  padding: '13px 14px',
  textAlign: 'left' as const,
  borderRadius: '7px',
  color: adminTokens.nav,
  display: 'flex',
  gap: '13px',
  alignItems: 'center',
  margin: '3px 0',
  font: 'inherit',
  fontSize: 16,
  cursor: 'pointer',
  textDecoration: 'none'
};

const NavLinks = ({
  items,
  compact,
  badge,
  onNavigate
}: {
  items: NavItem[];
  compact: boolean;
  badge: number;
  onNavigate?: () => void;
}) => {
  const location = useLocation();

  return (
    <Box component="nav">
      {items.map((item) => {
        const selected = isNavActive(location.pathname, item.path);
        const showBadge = item.badge === 'entries' && badge > 0;
        const button = (
          <Box
            component={Link}
            to={item.path}
            aria-current={selected ? 'page' : undefined}
            onClick={onNavigate}
            sx={{
              ...navButtonSx,
              justifyContent: compact ? 'center' : 'flex-start',
              px: compact ? '13px' : '14px',
              ...(selected
                ? {
                    backgroundColor: adminTokens.activeBg,
                    color: adminTokens.pink,
                    fontWeight: 700
                  }
                : {}),
              '&:hover': {filter: 'brightness(0.96)', textDecoration: 'none'}
            }}
          >
            <Box component="span" sx={{display: 'flex', width: 19, flexShrink: 0}}>
              <NavIcon name={item.icon} size={compact ? 22 : 19} />
            </Box>
            {!compact && <Box component="span">{item.label}</Box>}
            {!compact && showBadge && (
              <Box
                component="span"
                sx={{
                  ml: 'auto',
                  background: adminTokens.badgeBg,
                  color: adminTokens.pink,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '3px 6px',
                  borderRadius: '5px'
                }}
              >
                {badge}
              </Box>
            )}
          </Box>
        );

        if (!compact) return <React.Fragment key={item.path}>{button}</React.Fragment>;

        return (
          <Tooltip key={item.path} title={item.label} placement="right">
            {button}
          </Tooltip>
        );
      })}
    </Box>
  );
};

const SectionLabel = ({children}: {children: string}) => (
  <Box
    sx={{
      fontSize: 12,
      letterSpacing: '1.5px',
      color: adminTokens.navLabel,
      margin: '6px 12px 10px',
      fontWeight: 700
    }}
  >
    {children}
  </Box>
);

const AdminSidebar = ({compact, onNavigate}: {compact: boolean; onNavigate?: () => void}) => {
  const canReadEntries = auth.hasPermission('entry:read');
  const {data} = useQuery<{total?: {count?: number}}>(PENDING_ENTRIES, {skip: !canReadEntries});
  const pending = data?.total?.count ?? 0;
  const workspace = visibleNav(workspaceNav);
  const administration = auth.isAdmin() ? visibleNav(adminNav) : [];

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden'}}>
      <Box
        component={Link}
        to="/"
        aria-label="LiveZone home"
        onClick={onNavigate}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start',
          flexShrink: 0,
          textDecoration: 'none',
          px: compact ? 0 : 0.5,
          pb: compact ? '16px' : '20px',
          overflow: 'hidden',
          '&:hover': {textDecoration: 'none'}
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="LiveZone"
          sx={{
            height: compact ? 28 : 34,
            width: compact ? 28 : 'auto',
            maxWidth: '100%',
            objectFit: 'cover',
            objectPosition: 'left center',
            display: 'block',
            mixBlendMode: 'multiply'
          }}
        />
      </Box>

      <Box sx={{flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain'}}>
        {!compact && <SectionLabel>WORKSPACE</SectionLabel>}
        <NavLinks items={workspace} compact={compact} badge={pending} onNavigate={onNavigate} />

        {administration.length > 0 && (
          <Box sx={{mt: compact ? 1 : '28px'}}>
            {!compact && <SectionLabel>ADMINISTRATION</SectionLabel>}
            <NavLinks items={administration} compact={compact} badge={pending} onNavigate={onNavigate} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminSidebar;
